import React, { useState } from "react";
import axios from "axios";
import { CalendarHeart, Check, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { id: "morning", label: "Morning" },
  { id: "afternoon", label: "Afternoon" },
  { id: "evening", label: "Evening" },
];

export const BookConsultationDialog = ({ plan, open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferredDate: "", timeWindow: "morning", note: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await axios.post(`${API}/consultations`, { ...form, plan: plan?.id || "" });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) {
      setDone(false);
      setError("");
      setForm({ name: "", email: "", phone: "", preferredDate: "", timeWindow: "morning", note: "" });
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="consultation-dialog" className="sm:max-w-md rounded-3xl">
        {done ? (
          <div className="py-8 text-center" data-testid="consultation-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft">
              <Check className="h-7 w-7 text-primary" />
            </div>
            <h3 className="mt-5 font-serif text-2xl text-foreground">You're booked.</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              We received your consultation request for the <span className="font-medium text-foreground">{plan?.name}</span> plan.
              We'll confirm your time by email shortly.
            </p>
            <button
              data-testid="consultation-close-btn"
              onClick={() => handleOpenChange(false)}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-gentle hover:bg-primary/90"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                <CalendarHeart className="h-5 w-5 text-primary" /> Book a consultation
              </DialogTitle>
              <DialogDescription>
                {plan?.name} plan — {plan?.price} {plan?.period}. We'll walk you through everything, including your one-time deployment fee.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Your name</Label>
                  <Input id="c-name" data-testid="consultation-name-input" value={form.name} onChange={set("name")} placeholder="Jane Moore" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-phone">Phone (optional)</Label>
                  <Input id="c-phone" data-testid="consultation-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" data-testid="consultation-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-date">Preferred date</Label>
                <Input id="c-date" data-testid="consultation-date-input" type="date" min={today} value={form.preferredDate} onChange={set("preferredDate")} required />
              </div>
              <div className="space-y-1.5">
                <Label>Time window</Label>
                <div className="grid grid-cols-3 gap-2">
                  {WINDOWS.map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      data-testid={`consultation-window-${w.id}`}
                      onClick={() => setForm((f) => ({ ...f, timeWindow: w.id }))}
                      className={`rounded-full border-2 px-3 py-2 text-sm transition-gentle ${
                        form.timeWindow === w.id
                          ? "border-primary bg-primary-soft/50 text-primary font-medium"
                          : "border-border text-foreground/70 hover:border-primary/40"
                      }`}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-note">Anything we should know? (optional)</Label>
                <Textarea id="c-note" data-testid="consultation-note-input" value={form.note} onChange={set("note")} placeholder="Tell us a little about your family or organization…" rows={3} />
              </div>
              {error && (
                <p data-testid="consultation-error" className="text-sm text-destructive">{error}</p>
              )}
              <button
                type="submit"
                data-testid="consultation-submit-btn"
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-gentle hover:bg-primary/90 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Request my consultation
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
